import { type FileWithPath } from 'react-dropzone';
import { type Asset } from '@/services/db/asset/entity';

export type RejectionReason = 'file-size' | 'image-dimensions';

export type AssetProcessedFile = {
  _file?: FileWithPath;
  path: string;
  name: string;
  type: string;
  size: number;
  state: 'PENDING' | 'SUCCESS' | 'FAILED' | 'UPLOADING';
  reason?: string;
  rejectionReasons?: Set<RejectionReason>;
};

export type AllowedFileCategory = 'image' | 'doc';

export type AssetUploadState = {
  allowedFileCategories?: AllowedFileCategory[];
  pendingFiles?: FileWithPath[];
  acceptedFiles?: AssetProcessedFile[];
  rejectedFiles?: AssetProcessedFile[];
  parentFolderId?: string | null;
  uploadedAssets?: Asset[];
  parentFolderName?: string;
  onUpload?: (files: Asset[]) => void;
};

export const IMAGE_EXTENSIONS = new Set(['bmp', 'gif', 'jpeg', 'jpg', 'png']);

export const IMAGE_MIME_TYPES = new Set(['image/bmp', 'image/gif', 'image/jpeg', 'image/png']);

export const DOC_MIME_TYPES = new Set(['application/pdf']);

export function isTypeAllowed(allowedFileCategories: AllowedFileCategory[] | undefined, file: FileWithPath) {
  const categories = allowedFileCategories ?? ['image', 'doc'];
  if (categories.includes('image') && IMAGE_MIME_TYPES.has(file.type)) return true;
  if (categories.includes('doc') && DOC_MIME_TYPES.has(file.type)) return true;
  return false;
}

export function isTypeSupported(file: FileWithPath) {
  return IMAGE_MIME_TYPES.has(file.type) || DOC_MIME_TYPES.has(file.type);
}

export function isOversizedImage(file: AssetProcessedFile): boolean {
  return (
    IMAGE_MIME_TYPES.has(file.type) &&
    file.rejectionReasons?.has('file-size') === true &&
    file.rejectionReasons.size === 1
  );
}

export function isImageAsset(asset: Asset): boolean {
  const extension = asset.name.split('.').pop()?.toLowerCase() ?? '';
  return IMAGE_EXTENSIONS.has(extension);
}
